import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import ReusableTable from "../../dashboard/ReusableTable";
import ReusableForm from "../../common/ReusableForm";
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from 'primereact/dropdown';
import { getRequest, postRequest } from '../../services/apiService';
import { XCircle } from 'lucide-react'; // Import XCircle icon

const DepartmentWiseMapping = ({ onBack }) => {
  const toast = useRef(null);
  const [departmentUserMappings, setDepartmentUserMappings] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentMapping, setCurrentMapping] = useState(null);
  const [mappingToDelete, setMappingToDelete] = useState(null);

  const [dropdownData, setDropdownData] = useState({
    departments: [],
    users: []
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    id: null,
    department: "",
    user: "",
  });

  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirmation",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  const [deleteFormData, setDeleteFormData] = useState({
    confirmation: ""
  });
  const [selectedDepartmentFilter, setSelectedDepartmentFilter] = useState(null);
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  // Fetch initial data
  useEffect(() => {
    fetchMappings();
    fetchDropdownData();
  }, [currentPage, selectedDepartmentFilter, search, rowsPerPage]);

  const fetchMappings = async () => {
    setLoading(true);
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(async () => {
      try {
        let endpoint = `/config/user-department-mapping/`;
        const response = await getRequest(endpoint);
        let filteredData = response.data || [];

        if (selectedDepartmentFilter) {
          filteredData = filteredData.filter(mapping => mapping.department.id === selectedDepartmentFilter);
        }

        if (search) {
          const lowerCaseSearch = search.toLowerCase();
          filteredData = filteredData.filter(mapping =>
            mapping.department.name.toLowerCase().includes(lowerCaseSearch) ||
            mapping.user.loginname.toLowerCase().includes(lowerCaseSearch)
          );
        }

        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        setDepartmentUserMappings(filteredData.slice(startIndex, endIndex));
        setTotalRecords(filteredData.length);
      } catch (error) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch department user mappings.",
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const fetchDropdownData = async () => {
    try {
      const [departmentsResponse, usersResponse] = await Promise.all([
        getRequest("/master/departments/"),
        getRequest("/api/auth/users/")
      ]);

      setDropdownData({
        departments: departmentsResponse.data.map(dept => ({ id: dept.id, name: dept.name })),
        users: usersResponse.results.data.map(user => ({ id: user.id, name: user.loginname }))
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch dropdown data.",
        life: 3000,
      });
    }
  };

  const mappingFormFields = [
    {
      name: "department",
      label: "Department",
      type: "select",
      required: true,
      options: [
        { label: "Select Department", value: "" },
        ...(dropdownData.departments || []).map(dept => ({
          label: dept.name,
          value: dept.id
        }))
      ],
      helpText: "Select the department"
    },
    {
      name: "user",
      label: "User Name",
      type: "select",
      required: true,
      options: [
        { label: "Select User", value: "" },
        ...(dropdownData.users || []).map(user => ({
          label: user.name,
          value: user.id
        }))
      ],
      helpText: "Select the user for this department"
    },
  ];

  const handleSave = async () => {
    if (!formData.department || !formData.user) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please fill in all required fields",
        life: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      if (formData.id) {
        // Update existing mapping using POST method with ID in payload
        await postRequest(`/config/user-department-mapping/`, {
          id: formData.id,
          user: formData.user,
          department: formData.department,
        });
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Mapping updated successfully!",
          life: 3000,
        });
      } else {
        // Add new mapping using POST method with status 1
        await postRequest("/config/user-department-mapping/", {
          user: formData.user,
          department: formData.department,
          status: 1 // Status 1 for new posts
        });
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Mapping added successfully!",
          life: 3000,
        });
        setCurrentPage(1);
      }
      setShowAddModal(false);
      setShowEditModal(false);
      setFormData({ id: null, department: "", user: "" });
      fetchMappings(); // Re-fetch data to update table
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to save mapping: ${error.response?.data?.message || error.message}`,
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setFormData({ id: null, department: "", user: "" });
    setShowAddModal(true);
  };

  const openEditModal = (mapping) => {
    setFormData({
      id: mapping.id,
      department: mapping.department?.id || "",
      user: mapping.user?.id || "",
    });
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please type 'DELETE' to confirm",
        life: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      // For delete, use POST with id and status 3 in payload
      await postRequest(`/config/user-department-mapping/`, {
        id: mappingToDelete.id,
        status: 3
      });
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Mapping deleted successfully!",
        life: 3000,
      });
      setShowDeleteModal(false);
      setMappingToDelete(null);
      setDeleteFormData({ confirmation: "" });
      fetchMappings(); // Re-fetch data to update table
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to delete mapping: ${error.response?.data?.message || error.message}`,
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  const openDeleteModal = (mapping) => {
    setMappingToDelete(mapping);
    setDeleteFormData({ confirmation: "" });
    setShowDeleteModal(true);
  };

  const columnFields = [
    {
      field: "department.name",
      header: "Department Name",
      body: (rowData) => rowData.department?.name || '-'
    },
    {
      field: "user.loginname",
      header: "User Name",
      body: (rowData) => rowData.user?.loginname || '-'
    },
  ];

  const actionItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: (rowData) => openEditModal(rowData),
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: (rowData) => openDeleteModal(rowData),
    },
  ];

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  return (
    <div className="min-h-screen bg-gray-100 py-4 px-4 relative">
      <Toast ref={toast} />

      <div className="max-w-full mx-auto">
        {/* Updated structure for filter and close button */}
        <div className="mb-4 p-fluid flex justify-between items-center"> {/* Added items-center for vertical alignment */}
          {/* Filter by Department on the left */}
          <Dropdown
            value={selectedDepartmentFilter}
            options={[
              { label: "All Departments", value: null },
              ...(dropdownData.departments || []).map(dept => ({
                label: dept.name,
                value: dept.id
              }))
            ]}
            onChange={(e) => {
              setSelectedDepartmentFilter(e.value);
              setCurrentPage(1);
            }}
            placeholder="Filter by Department"
            showClear
            className="w-64 md:w-14rem text-center border-1 border-black"
          />

          {/* Close Button on the right */}
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors text-gray-600 z-50"
              title="Close"
            >
              <XCircle size={20} />
            </button>
          )}
        </div>
        <div className="mt-0">
          <ReusableTable
            title="Department User Mapping"
            data={departmentUserMappings}
            setData={setDepartmentUserMappings}
            columnFields={columnFields}
            setAddNewDialogVisible={openAddModal}
            actionMenu={true}
            actionItems={actionItems}
            loading={loading}
            totalRecords={totalRecords}
            rows={rowsPerPage}
            onPageChange={onPageChange}
            first={(currentPage - 1) * rowsPerPage}
            emptyMessage="No department-user mappings found. Add new mappings to get started."
            searchValue={search}
            onSearchChange={handleTableSearch}
          />
        </div>

        {/* Add/Edit Mapping Dialog */}
        {(showAddModal || showEditModal) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className={`bg-white rounded-xl shadow-2xl w-full mx-4 animate-fade-in max-w-2xl`}>
              <ReusableForm
                formData={formData}
                setFormData={setFormData}
                fields={mappingFormFields}
                onSubmit={handleSave}
                onHide={() => (showEditModal ? setShowEditModal(false) : setShowAddModal(false))}
                loading={loading}
                headerText={showEditModal ? "Edit Department User Mapping" : "Add Department User Mapping"}
              />
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <ReusableDeleteDialog
          visible={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onDelete={handleDelete}
          fields={deleteFormFields}
          formData={deleteFormData}
          setFormData={setDeleteFormData}
          loading={loading}
          title="Delete Department User Mapping"
          description={`Are you sure you want to delete the mapping for department "${mappingToDelete?.department?.name}" and user "${mappingToDelete?.user?.loginname}"? This action cannot be undone. Type 'DELETE' to confirm.`}
        />
      </div>
    </div>
  );
};

export default DepartmentWiseMapping;